import React, { useEffect, useMemo, useState } from 'react';
import { SidePanel } from '../panel/SidePanel';
import { FormField } from '../panel/FormField';
import { SmartSelect, SearchAhead, DateField, TimeField } from '../dropdown';
import type { SmartSelectOption } from '../dropdown';
import type {
  Todo,
  CreateTodoRequest,
  UpdateTodoRequest,
  TodoStatus,
  TodoPriority,
} from '@techgitter/contracts';
import type { CustomerIndexItemDto, WorkItem } from '@techgitter/contracts';
import { useUsers } from '../../hooks/useUsers';
import { getCustomerIndex } from '../../services/customerIndexApi';
import { searchWorkItems } from '../../services/workItemDataService';
import { buildUserColorIndex, resolveUserColor } from '../../utils/userColorIndex';
import {
  STATUS_OPTIONS,
  PRIORITY_OPTIONS,
  toDateTimeLocalValue,
  fromDateTimeLocalValue,
} from '../../utils/todoDisplay';
import { FollowUpTicketSummary } from './FollowUpTicketSummary';
import './ToDoTaskPanel.css';

/** Split a `yyyy-MM-ddTHH:mm` local datetime into its date / time halves and
 *  recombine them — drives the DateField + TimeField pair off the single
 *  datetime string the API round-trips. A missing time defaults to midnight; an
 *  empty date clears the whole value (a time alone has no date to anchor it). */
const datePartOf = (v: string): string => (v ? v.slice(0, 10) : '');
const timePartOf = (v: string): string => (v.length >= 16 ? v.slice(11, 16) : '');
const combineDateTime = (date: string | null, time: string | null): string =>
  date ? `${date}T${time || '00:00'}` : '';

/** Initials (max 2) from a display name, for the Assign-To avatar. */
const initialsOf = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('') || '?';

interface CustomerOption {
  id: string;
  label: string;
}

interface WorkItemOption {
  id: string;
  label: string;
}

/** SearchAhead result for the Work Item field — carries the work-item id so a
 *  pick can be resolved back to {id, label}. */
interface WorkItemResult {
  code: string;
  description: string;
  id: string;
}

interface ToDoTaskPanelProps {
  /** The task being edited, or null when creating. */
  todo: Todo | null;
  isSaving: boolean;
  error: string | null;
  onClose: () => void;
  onSave: (payload: CreateTodoRequest | UpdateTodoRequest) => Promise<void>;
}

/**
 * Right-aligned panel for creating / editing a To-Do task. Reuses the shared
 * `SidePanel` shell + `FormField` controls (the former bespoke
 * `.todo-panel__*` chrome has been retired), so it reads identically to the
 * calendar Schedule Appointment / Add Leave and Repair Services panels. Lives in
 * the right column of `ToDoTasksAdminPage`'s `.todo-layout` grid.
 */
export const ToDoTaskPanel: React.FC<ToDoTaskPanelProps> = ({
  todo,
  isSaving,
  error,
  onClose,
  onSave,
}) => {
  const isEdit = todo !== null;

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<TodoPriority>('medium');
  const [status, setStatus] = useState<TodoStatus>('open');
  const [dueLocal, setDueLocal] = useState('');
  const [scheduledLocal, setScheduledLocal] = useState('');
  const [assigneeId, setAssigneeId] = useState<string>('');
  const [customer, setCustomer] = useState<CustomerOption | null>(null);
  const [workItem, setWorkItem] = useState<WorkItemOption | null>(null);

  // Billable charge (one-time charge to the customer's monthly invoice on completion)
  const [billingEnabled, setBillingEnabled] = useState(false);
  const [billName, setBillName] = useState('');
  const [billQty, setBillQty] = useState('1');
  const [billPrice, setBillPrice] = useState('0');
  const [billDesc, setBillDesc] = useState('');

  const { users, isLoading: usersLoading } = useUsers();

  // Customer options (loaded once when the panel mounts)
  const [customerOptions, setCustomerOptions] = useState<CustomerOption[]>([]);
  useEffect(() => {
    const controller = new AbortController();
    getCustomerIndex(controller.signal)
      .then((res) => {
        setCustomerOptions(
          res.items.map((c: CustomerIndexItemDto) => ({
            id: c.dataverseCustomerId,
            label: c.displayName,
          })),
        );
      })
      .catch(() => {/* lookup is optional — ignore load errors */});
    return () => controller.abort();
  }, []);

  // Work item options — searched on demand
  const [workItemInput, setWorkItemInput] = useState('');
  const [workItemOptions, setWorkItemOptions] = useState<WorkItemOption[]>([]);
  useEffect(() => {
    const term = workItemInput.trim();
    if (term.length < 2) {
      setWorkItemOptions([]);
      return;
    }
    const controller = new AbortController();
    const handle = setTimeout(() => {
      searchWorkItems(term, controller.signal)
        .then((items: WorkItem[]) => {
          setWorkItemOptions(
            items.map((w) => ({
              id: w.guid,
              label: `${w.number ? `#${w.number} ` : ''}${w.title}`.trim(),
            })),
          );
        })
        .catch(() => {/* optional — ignore */});
    }, 300);
    return () => {
      clearTimeout(handle);
      controller.abort();
    };
  }, [workItemInput]);

  // Seed form state when the panel opens / target changes
  useEffect(() => {
    if (todo) {
      setTitle(todo.title ?? '');
      setDescription(todo.description ?? '');
      setPriority(todo.priority ?? 'medium');
      setStatus(todo.status ?? 'open');
      setDueLocal(toDateTimeLocalValue(todo.dueDate));
      setScheduledLocal(toDateTimeLocalValue(todo.scheduledDate));
      setAssigneeId(todo.assignedToId ?? '');
      setCustomer(todo.customer ? { id: todo.customer.id, label: todo.customer.name ?? todo.customer.id } : null);
      const wiLabel = todo.workItem
        ? `${todo.workItem.number ? `#${todo.workItem.number} ` : ''}${todo.workItem.title ?? todo.workItem.id}`.trim()
        : '';
      setWorkItem(todo.workItem ? { id: todo.workItem.id, label: wiLabel } : null);
      setWorkItemInput(wiLabel);
      setBillingEnabled(!!todo.billing);
      setBillName(todo.billing?.name ?? '');
      setBillQty(String(todo.billing?.quantity ?? 1));
      setBillPrice(String(todo.billing?.priceRetail ?? 0));
      setBillDesc(todo.billing?.description ?? '');
    } else {
      setTitle('');
      setDescription('');
      setPriority('medium');
      setStatus('open');
      setDueLocal('');
      setScheduledLocal('');
      setAssigneeId('');
      setCustomer(null);
      setWorkItem(null);
      setWorkItemInput('');
      setBillingEnabled(false);
      setBillName('');
      setBillQty('1');
      setBillPrice('0');
      setBillDesc('');
    }
  }, [todo]);

  // Escape closes the panel. (When a dropdown is open it consumes Escape and
  // stops propagation, so only the dropdown closes.)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const trimmedTitle = title.trim();
  const billPriceNum = Number(billPrice);
  const billQtyNum = Number(billQty);
  const billingValid =
    !billingEnabled ||
    (!!customer && billName.trim().length > 0 && Number.isFinite(billPriceNum) && billQtyNum > 0);
  const isValid = trimmedTitle.length > 0 && billingValid;

  // Assign-To options + avatar render-item (colour resolved by user id so it
  // matches the avatar shown for that user everywhere else in the app).
  const assigneeOptions = useMemo<SmartSelectOption[]>(
    () => users.map((u) => ({ value: u.guid, label: u.fullname || u.email })),
    [users],
  );
  const colorIndex = useMemo(() => buildUserColorIndex(users), [users]);
  const renderAssignee = useMemo(
    () => (option: SmartSelectOption) => {
      const colour = resolveUserColor(colorIndex, { id: option.value, name: option.label });
      return (
        <>
          <span
            className="sm4rt-select__avatar"
            style={{ background: colour.bg, color: colour.fg }}
          >
            {initialsOf(option.label)}
          </span>
          <span className="sm4rt-dd__label">{option.label}</span>
        </>
      );
    },
    [colorIndex],
  );

  // Customer SmartSelect options — keep the seeded customer present even if it
  // isn't in the loaded index, so its name still renders in the closed trigger.
  const customerSelectOptions = useMemo<SmartSelectOption[]>(() => {
    const opts = customerOptions.map((c) => ({ value: c.id, label: c.label }));
    if (customer && !opts.some((o) => o.value === customer.id)) {
      return [{ value: customer.id, label: customer.label }, ...opts];
    }
    return opts;
  }, [customerOptions, customer]);

  const workItemResults = useMemo<WorkItemResult[]>(
    () => workItemOptions.map((o) => ({ code: o.label, description: '', id: o.id })),
    [workItemOptions],
  );

  const isFollowUpTicket = todo?.outputKind === 'ticket';

  const handleSave = async () => {
    if (!isValid) return;
    const base = {
      title: trimmedTitle,
      description: description.trim() || undefined,
      priority,
      dueDate: fromDateTimeLocalValue(dueLocal),
      scheduledDate: fromDateTimeLocalValue(scheduledLocal),
      assignedToId: assigneeId || undefined,
      customerId: customer?.id,
      workItemId: workItem?.id,
      billing: billingEnabled && customer
        ? {
            name: billName.trim(),
            quantity: billQtyNum || 1,
            priceRetail: billPriceNum || 0,
            description: billDesc.trim() || undefined,
          }
        : undefined,
    };
    const payload: CreateTodoRequest | UpdateTodoRequest = isEdit
      ? { ...base, status }
      : base;
    await onSave(payload);
  };

  return (
    <SidePanel
      title={isEdit ? 'Edit task' : 'New task'}
      submitLabel={isSaving ? 'Saving…' : isEdit ? 'Save changes' : 'Add task'}
      isValid={isValid && !isSaving}
      onClose={onClose}
      onSubmit={handleSave}
    >
      {error && <div className="side-panel__alert side-panel__alert--error">{error}</div>}

      <FormField label="Title *">
        <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} />
      </FormField>

      <FormField label="Description">
        <textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
      </FormField>

      {isFollowUpTicket && todo && <FollowUpTicketSummary todo={todo} />}

      {/* Priority + Status */}
      {isEdit ? (
        <div className="side-panel__field-row">
          <SmartSelect
            label="Priority"
            value={priority}
            options={PRIORITY_OPTIONS}
            onChange={(v) => v && setPriority(v as TodoPriority)}
          />
          <SmartSelect
            label="Status"
            value={status}
            options={STATUS_OPTIONS}
            onChange={(v) => v && setStatus(v as TodoStatus)}
          />
        </div>
      ) : (
        <SmartSelect
          label="Priority"
          value={priority}
          options={PRIORITY_OPTIONS}
          onChange={(v) => v && setPriority(v as TodoPriority)}
        />
      )}

      {/* Scheduled date + time */}
      <div className="side-panel__field-row">
        <DateField
          label="Scheduled date"
          value={datePartOf(scheduledLocal) || null}
          onChange={(iso) => setScheduledLocal(combineDateTime(iso, timePartOf(scheduledLocal)))}
        />
        <TimeField
          label="Scheduled time"
          value={timePartOf(scheduledLocal) || null}
          onChange={(hhmm) => setScheduledLocal(combineDateTime(datePartOf(scheduledLocal), hhmm))}
        />
      </div>

      {/* Due date + time */}
      <div className="side-panel__field-row">
        <DateField
          label="Due date"
          value={datePartOf(dueLocal) || null}
          onChange={(iso) => setDueLocal(combineDateTime(iso, timePartOf(dueLocal)))}
        />
        <TimeField
          label="Due time"
          value={timePartOf(dueLocal) || null}
          onChange={(hhmm) => setDueLocal(combineDateTime(datePartOf(dueLocal), hhmm))}
        />
      </div>

      {/* Assign To */}
      <SmartSelect
        label="Assign To"
        value={assigneeId || null}
        options={assigneeOptions}
        onChange={(val) => setAssigneeId(val ?? '')}
        renderItem={renderAssignee}
        placeholder={usersLoading ? 'Loading…' : 'Select…'}
        disabled={usersLoading}
      />

      {/* Customer */}
      <SmartSelect
        label="Customer (optional)"
        value={customer?.id ?? null}
        options={customerSelectOptions}
        onChange={(val) => {
          if (!val) {
            setCustomer(null);
            return;
          }
          const found = customerOptions.find((c) => c.id === val);
          setCustomer(found ?? customer ?? { id: val, label: val });
        }}
        placeholder="Select a customer…"
      />

      {/* Work item */}
      <SearchAhead
        label="Work item (optional)"
        placeholder="Search work items…"
        value={workItemInput}
        results={workItemResults}
        onSearch={(text) => {
          setWorkItemInput(text);
          if (!text) setWorkItem(null);
        }}
        onSelect={(r) => {
          setWorkItem({ id: r.id, label: r.code });
          setWorkItemInput(r.code);
        }}
      />

      {/* Billable charge on completion — not for a follow-up ticket, whose
          charges are on the ticket it raises; offering both invites billing twice. */}
      {!isFollowUpTicket && (
        <>
          <label className="side-panel__checkbox">
            <input
              type="checkbox"
              checked={billingEnabled}
              onChange={(e) => setBillingEnabled(e.target.checked)}
            />
            Add billable charge on completion
          </label>

          {billingEnabled && (
            <>
              {!customer && (
                <div className="side-panel__alert side-panel__alert--warning">
                  Select a customer above to bill this charge.
                </div>
              )}
              <p className="todo-hint">
                When this task is marked done, a one-time charge is added to the customer&apos;s monthly invoice schedule.
              </p>
              <FormField label="Charge item / SKU *">
                <input value={billName} onChange={(e) => setBillName(e.target.value)} />
              </FormField>
              <div className="side-panel__field-row">
                <FormField label="Qty">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={billQty}
                    onChange={(e) => setBillQty(e.target.value)}
                    style={{ textAlign: 'right' }}
                  />
                </FormField>
                <FormField label="Unit price $">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={billPrice}
                    onChange={(e) => setBillPrice(e.target.value)}
                    style={{ textAlign: 'right' }}
                  />
                </FormField>
              </div>
              <FormField label="Charge description (optional)">
                <input value={billDesc} onChange={(e) => setBillDesc(e.target.value)} />
              </FormField>
            </>
          )}
        </>
      )}
    </SidePanel>
  );
};
